import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader, XCircle } from "lucide-react";
import api from "@/lib/api";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "polling", engagement_id: "" });

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { navigate("/command"); return undefined; }
    let ticks = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.project_id) setState((s) => ({ ...s, project_id: r.data.project_id }));
        if (r.data.payment_status === "paid") {
          setState({ status: "paid", engagement_id: r.data.engagement_id, project_id: r.data.project_id, kind: r.data.kind });
          return true;
        }
      } catch { /* keep polling */ }
      return false;
    };
    const id = setInterval(async () => {
      ticks += 1;
      const done = await poll();
      if (done || ticks > 30) {
        clearInterval(id);
        setState((s) => (s.status === "paid" ? s : { status: "timeout", engagement_id: "", project_id: "" }));
      }
    }, 2000);
    poll();
    return () => clearInterval(id);
  }, [params, navigate]);

    const isCreator = state.kind === "creator_fee";
    const dest = isCreator
      ? { to: "/creators", label: "Open Your Creator Forge" }
      : state.project_id
        ? { to: "/portal", label: "Open Client Portal" }
        : { to: `/command?engagement=${state.engagement_id}`, label: "Open Project Workspace" };

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-[#050505] text-zinc-100 flex items-center justify-center p-4">
      <div className="border border-white/10 bg-black/40 max-w-md w-full p-8 text-center flex flex-col items-center gap-4">
        {state.status === "paid" ? (
          <>
            <CheckCircle2 className="w-12 h-12 text-nc-green" />
            <h1 className="font-sans font-bold uppercase tracking-wide text-xl">
              {isCreator ? "Creator Forge Activated" : state.project_id ? "Deposit Secured" : "Escrow Funded"}
            </h1>
            <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">
              {isCreator
                ? "Payment confirmed by Stripe. Your Local Forge Kit is unlocked — local TRELLIS setup, rig-prep standards and direct upload are waiting in your dashboard. Finish the Creator Academy and your first approved pack pays your fee back."
                : state.project_id
                  ? "Payment confirmed by Stripe. Your project deposit is recorded and the studio has been cleared to begin production."
                  : "Payment confirmed by Stripe. Funds are held in platform escrow and the contractor has been cleared to begin work."}
            </p>
            <Link to={dest.to} data-testid="payment-success-command" className="bg-nc-green hover:bg-nc-green/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-6 py-3 transition-colors">
              {dest.label}
            </Link>
          </>
        ) : state.status === "timeout" ? (
          <>
            <XCircle className="w-12 h-12 text-nc-orange" />
            <h1 className="font-sans font-bold uppercase tracking-wide text-xl">Confirmation Pending</h1>
            <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">
              Stripe has not confirmed the payment yet — it can take a moment. Check back shortly.
            </p>
            <Link to={state.project_id ? "/portal" : "/command"} className="border border-white/20 text-zinc-200 font-mono uppercase tracking-[0.2em] text-[10px] px-6 py-3 transition-colors">
              {state.project_id ? "Back to Client Portal" : "Back to Projects"}
            </Link>
          </>
        ) : (
          <>
            <Loader className="w-12 h-12 text-nc-blue animate-spin" />
            <h1 className="font-sans font-bold uppercase tracking-wide text-xl">Verifying Payment</h1>
            <p data-testid="payment-verifying" className="font-mono text-[11px] text-zinc-400">Confirming with Stripe…</p>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  const [params] = useSearchParams();
  const eng = params.get("engagement");
  const proj = params.get("project");
  const back = proj ? "/portal" : eng ? `/command?engagement=${eng}` : "/command";
  return (
    <div data-testid="payment-cancel-page" className="min-h-screen bg-[#050505] text-zinc-100 flex items-center justify-center p-4">
      <div className="border border-white/10 bg-black/40 max-w-md w-full p-8 text-center flex flex-col items-center gap-4">
        <XCircle className="w-12 h-12 text-zinc-500" />
        <h1 className="font-sans font-bold uppercase tracking-wide text-xl">Checkout Cancelled</h1>
        <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">No charge was made. The escrow stays unfunded until you complete checkout.</p>
        <Link to={back} data-testid="payment-cancel-back" className="bg-nc-blue hover:bg-nc-blue/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-6 py-3 transition-colors">
          {proj ? "Back to Client Portal" : "Back to Projects"}
        </Link>
      </div>
    </div>
  );
}
